/**
 * Common filter models used across modules (Work Packages, Inspections, Actions, Milestone Tracker, etc.)
 * Reusable operator definitions and filter selection interfaces.
 */
import { FilterOperator } from '../services/views/views.enums';

/**
 * Defines the shape of a single operator entry shown in the filter UI.
 * Renamed from FilterOperator to avoid naming conflict with the FilterOperator enum.
 */
export interface FilterOperatorDef {
  value: string;
  label: string;
  requiresSecondValue?: boolean; // For 'between' operator
  multiValue?: boolean;          // For 'in', 'notin' operators
}

export interface FilterSelection {
  fieldKey: string;
  dataType: string;
  operator: string;
  value: any; // single value, [start, end] for between, or array for in/notin
}

/** Operators that require no value input (null checks). */
const NO_VALUE_OPERATORS: FilterOperatorDef[] = [
  { value: FilterOperator.IS_NOT_NULL, label: 'Contains data' },
  { value: FilterOperator.IS_NULL, label: 'Does not contain data' },
];

export const FILTER_OPERATORS_BY_TYPE: Record<string, FilterOperatorDef[]> = {
  // Text-based types
  text: [
    { value: FilterOperator.CONTAINS, label: 'Contains' },
    { value: FilterOperator.EQUALS, label: 'Equals' },
    { value: FilterOperator.NOT_EQUALS, label: 'Not equals' },
    { value: FilterOperator.IN, label: 'In list', multiValue: true },
    { value: FilterOperator.NOT_IN, label: 'Not in list', multiValue: true },
    ...NO_VALUE_OPERATORS,
  ],
  string: [
    { value: FilterOperator.CONTAINS, label: 'Contains' },
    { value: FilterOperator.EQUALS, label: 'Equals' },
    { value: FilterOperator.NOT_EQUALS, label: 'Not equals' },
    { value: FilterOperator.IN, label: 'In list', multiValue: true },
    { value: FilterOperator.NOT_IN, label: 'Not in list', multiValue: true },
    ...NO_VALUE_OPERATORS,
  ],
  textarea: [
    { value: FilterOperator.CONTAINS, label: 'Contains' },
    { value: FilterOperator.EQUALS, label: 'Equals' },
    { value: FilterOperator.NOT_EQUALS, label: 'Not equals' },
    { value: FilterOperator.IN, label: 'In list', multiValue: true },
    { value: FilterOperator.NOT_IN, label: 'Not in list', multiValue: true },
    ...NO_VALUE_OPERATORS,
  ],

  // Numeric types
  number: [
    { value: FilterOperator.EQUALS, label: 'Equals' },
    { value: FilterOperator.NOT_EQUALS, label: 'Not equals' },
    { value: FilterOperator.GREATER_THAN, label: 'Greater than' },
    { value: FilterOperator.GREATER_THAN_OR_EQUAL, label: 'Greater than or equal' },
    { value: FilterOperator.LESS_THAN, label: 'Less than' },
    { value: FilterOperator.LESS_THAN_OR_EQUAL, label: 'Less than or equal' },
    { value: FilterOperator.BETWEEN, label: 'Between', requiresSecondValue: true },
    ...NO_VALUE_OPERATORS,
  ],

  // Date/Time types
  datetime: [
    { value: FilterOperator.EQUALS, label: 'On' },
    { value: FilterOperator.NOT_EQUALS, label: 'Not on' },
    { value: FilterOperator.GREATER_THAN, label: 'After' },
    { value: FilterOperator.GREATER_THAN_OR_EQUAL, label: 'On or after' },
    { value: FilterOperator.LESS_THAN, label: 'Before' },
    { value: FilterOperator.LESS_THAN_OR_EQUAL, label: 'On or before' },
    { value: FilterOperator.BETWEEN, label: 'Between', requiresSecondValue: true },
    ...NO_VALUE_OPERATORS,
  ],
  date: [
    { value: FilterOperator.EQUALS, label: 'On' },
    { value: FilterOperator.NOT_EQUALS, label: 'Not on' },
    { value: FilterOperator.GREATER_THAN, label: 'After' },
    { value: FilterOperator.GREATER_THAN_OR_EQUAL, label: 'On or after' },
    { value: FilterOperator.LESS_THAN, label: 'Before' },
    { value: FilterOperator.LESS_THAN_OR_EQUAL, label: 'On or before' },
    { value: FilterOperator.BETWEEN, label: 'Between', requiresSecondValue: true },
    ...NO_VALUE_OPERATORS,
  ],

  // Dropdown/Select types
  dropdown: [
    { value: FilterOperator.IN, label: 'Is any of', multiValue: true },
    { value: FilterOperator.NOT_IN, label: 'Excludes any of', multiValue: true },
    ...NO_VALUE_OPERATORS,
  ],

  // Boolean type
  boolean: [
    { value: FilterOperator.EQUALS, label: 'Is' },
    ...NO_VALUE_OPERATORS,
  ],
};

/**
 * Default operator per data type (first applied when a filter is added).
 */
export const DEFAULT_OPERATOR_BY_TYPE: Record<string, string> = {
  text: FilterOperator.CONTAINS,
  string: FilterOperator.CONTAINS,
  textarea: FilterOperator.CONTAINS,
  number: FilterOperator.EQUALS,
  date: FilterOperator.EQUALS,
  datetime: FilterOperator.EQUALS,
  dropdown: FilterOperator.IN,
  boolean: FilterOperator.EQUALS,
};

/**
 * Get operators for a given data type, falls back to string/text operators.
 */
export function getOperatorsForType(dataType: string): FilterOperatorDef[] {
  return FILTER_OPERATORS_BY_TYPE[dataType] || FILTER_OPERATORS_BY_TYPE['string'];
}

/**
 * Get default operator for a given data type.
 */
export function getDefaultOperator(dataType: string): string {
  return DEFAULT_OPERATOR_BY_TYPE[dataType] || FilterOperator.CONTAINS;
}

/**
 * Check if operator is a 'between' type (requires two values).
 */
export function isBetweenOperator(operator: string): boolean {
  return operator === FilterOperator.BETWEEN;
}

/**
 * Check if operator is a multi-value type (in, notin).
 */
export function isMultiValueOperator(operator: string): boolean {
  return operator === FilterOperator.IN || operator === FilterOperator.NOT_IN;
}

/**
 * Check if operator requires no value input (isnull, isnotnull).
 */
export function isNoValueOperator(operator: string): boolean {
  return operator === FilterOperator.IS_NULL || operator === FilterOperator.IS_NOT_NULL;
}
